/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import {
  downloadCsvFile,
  generateFileName,
  downloadXlsxFile,
  downloadPdfFile,
} from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      reply_time: false,
      school_enrollments_count: false,
      school_defaulters_count: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      reply_time: [],
      school_enrollments_count: [],
      school_defaulters_count: [],
    },
  },
  labelFunnelReport: {
      isFetching: false,
      label_funnel_count:[],
      labels:[],
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    school_enrollments_count: 0,
    school_defaulters_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getLabelFunnelReports(_state) {
    return _state.labelFunnelReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchAccountLabelFunnelReport({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_LABEL_FUNNEL_REPORT_LOADING, true);
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      commit(types.default.SET_ACCOUNT_LABEL_FUNNEL_REPORTS, {
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_LABEL_FUNNEL_REPORT_LOADING, {
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type, reportObj.page)
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'agent_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'agent_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.getLabelReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'label_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'label_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'inbox_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'inbox_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'team_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'team_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV()
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadEnrollmentsReportsCSV(_, reportObj) {
    if(window.chatwootConfig.isKedu){
      return Report.getEnrollmentsReportsCSV(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'school_enrollment_csv',
        });
      })
      .catch(error => {
        console.error(error);
      });
    }
  },
  downloadEnrollmentsReportsXLSX(_, reportObj) {
    if(window.chatwootConfig.isKedu){
      return Report.getEnrollmentsReportsXLSX(reportObj)
      .then(response => {
        downloadXlsxFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'school_enrollments_xlsx',
        });
      })
      .catch(error => {
        console.error(error);
      });
    }
  },
  downloadContactsReportsCSV(_, reportObj) {
    return Report.getContactsReportsCSV(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'contact_csv',
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadContactsReportsXLSX(_, reportObj) {
    return Report.getContactsReportsXLSX(reportObj)
      .then(response => {
        downloadXlsxFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'contact_xlsx',
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadCampaignReports(_, reportObj) {
    return Report.getCampaignReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'campaign_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'campaign_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadEnrollmentsPerAgentReports(_, reportObj) {
    return Report.getEnrollmentsPerAgentReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'enrollments_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'enrollments_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadDefaultersPerAgentReports(_, reportObj) {
    return Report.getSchoolDefaultersPerAgentReports(reportObj)
      .then(response => {
        if (reportObj.fileType === "xlsx"){
          downloadXlsxFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'school_defaulters_xlsx',
          });
        } else {
          downloadCsvFile(reportObj.fileName, response.data);
          AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
            reportType: 'school_defaulters_csv',
            businessHours: reportObj?.businessHours,
          });
        }
      })
      .catch(error => {
        console.error(error);
      });
  },
  fetchConversationsExportEligible(_, reportObj) {
    return Report.getConversationsExportEligible(reportObj).then(
      response => response.data
    );
  },
  downloadConversationsExportPDF(_, reportObj) {
    return Report.downloadConversationsExportPDF(reportObj).then(response => {
      downloadPdfFile(reportObj.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'conversations_export_pdf',
      });
    });
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_ACCOUNT_LABEL_FUNNEL_REPORTS](_state, { data }) {
    _state.labelFunnelReport.data = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_ACCOUNT_LABEL_FUNNEL_REPORT_LOADING](_state, { value }) {
    _state.labelFunnelReport.isFetching = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
